// Voyalyo Age-1 – verbindlicher Alters-/Einschränkungsvertrag (Client-Seite).
// Quelle der Wahrheit ist AUSSCHLIESSLICH die Serverantwort (/auth/me + 403-Codes).
// Der Client setzt age_group / verified_age_group NIEMALS selbst.

export const AGE_GROUP = {
  UNDER_16: "under_16",
  MINOR_16_17: "minor_16_17",
  ADULT: "adult_18_plus",
};

export const AGE_STATUS = {
  UNVERIFIED: "unverified",
  PENDING: "pending",
  VERIFIED: "verified",
  REJECTED: "rejected",
  EXPIRED: "expired",
};

export const AGE_ERROR = {
  BELOW_MINIMUM: "age_below_minimum",
  VERIFICATION_REQUIRED: "age_verification_required",
  GROUP_RESTRICTED: "age_group_restricted",
  INVALID_BIRTH_DATE: "invalid_birth_date",
  BIRTH_DATE_LOCKED: "birth_date_locked",
  TRANSITION_PENDING: "age_transition_pending",
};

// Mindestalter für die Registrierung (serverseitige Entscheidung bleibt verbindlich).
export const MIN_AGE = 16;

export function ageFromBirthDate(v) {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a--;
  return a;
}

// Tolerant gegenüber möglichen Feldnamen (age_group bevorzugt, verified_age_group als Fallback).
export const getAgeGroup = (u) => u?.age_group ?? u?.verified_age_group ?? null;
export const getAgeStatus = (u) => u?.age_verification_status ?? null;

export const isPrivileged = (u) => ["admin", "moderator"].includes(u?.role);

// Autoritatives Server-Signal für die Freischalt-Sperre (Architektur: Pflichtverifizierung
// vor Freischaltung). Das Backend setzt account_restriction für ALLE nicht freigeschalteten
// Nutzer (inkl. unverifizierter Erwachsener und role=admin, sofern is_system_admin=false).
export const RESTRICTION_AGE_VERIFICATION = "age_verification_required";

// Eingeschränkt = Server meldet eine Freischalt-Sperre. Quelle der Wahrheit ist AUSSCHLIESSLICH
// die Serverantwort (/auth/me.account_restriction) und hat IMMER Vorrang – auch für role=admin/
// moderator. Grund: Das Backend erzwingt die Sperre via 403 auch für Nicht-System-Admins
// (z.B. info@voyalyo.de: role="admin", is_system_admin=false, account_restriction gesetzt).
// Würde der Client hier auf role="admin" privilegieren, entstünde ein Widerspruch (Discover
// rendert -> 403 -> Redirect -> Guard schickt zurück) => Flackern/Ping-Pong.
// Nur OHNE explizite Server-Sperre gelten Admin/Moderator als privilegiert. Fallback (falls
// Feld fehlt): 16/17 unverifiziert bzw. under_16.
export function isRestricted(u) {
  if (!u) return false;
  if (u.account_restriction === RESTRICTION_AGE_VERIFICATION) return true;
  if (isPrivileged(u)) return false;
  const g = getAgeGroup(u);
  if (g === AGE_GROUP.UNDER_16) return true;
  if (g === AGE_GROUP.MINOR_16_17 && getAgeStatus(u) !== AGE_STATUS.VERIFIED) return true;
  return false;
}

// Rückwärtskompatibler Alias (frühere Bezeichnung); identische, serverbasierte Logik.
export const isRestrictedMinor = isRestricted;

export const isAgeVerified = (u) => getAgeStatus(u) === AGE_STATUS.VERIFIED;

// Kanonische, EINZIGE Alters-Freigabe-Prüfung fürs gesamte Frontend. Kapselt exakt die
// bereits auf der AgeVerificationPage genutzte Bedingung: die frische, autoritative
// Antwort aus GET /verification/age ODER der /auth/me-Status. Wird von den Redirect-Guards
// (ProtectedRoute, resolveLandingRoute) verwendet, damit dieselbe Wahrheit gilt und ein
// bereits verifizierter Account nicht wegen eines veralteten /auth/me-Feldes umgeleitet wird.
// KEINE neue fachliche Logik – nur Kapselung. ageStatus ist optional (Fallback = /auth/me).
export const isAgeCleared = (user, ageStatus) =>
  ageStatus?.status === AGE_STATUS.VERIFIED || isAgeVerified(user);

// E-Mail-Verifizierung ist ein SEPARATER Zustand (getrennt von der Altersverifizierung).
// Greift AUSSCHLIESSLICH, wenn das Backend (/auth/me) email_verified === false liefert.
// Keine abweichende Prüfung pro Seite – dieser Helper ist die einzige Quelle der Wahrheit.
export const isEmailUnverified = (u) => u?.email_verified === false;

// Zentrale Auth-Landing-Route. Verbindliche Priorität:
// 1) E-Mail unbestätigt -> /e-mail-bestaetigen  2) Alterssperre -> /altersverifikation
// 3) sonst -> geschützte App (/entdecken). Wird von AuthPage, GoogleCallback und den
// Statusseiten genutzt, damit die Routing-Priorität an EINER Stelle definiert ist.
export function resolveLandingRoute(u, ageStatus) {
  if (isEmailUnverified(u)) return "/e-mail-bestaetigen";
  if (isRestricted(u) && !isAgeCleared(u, ageStatus)) return "/altersverifikation";
  return "/entdecken";
}

// Defensive Extraktion des maschinenlesbaren Codes aus einer axios-Fehlerantwort.
// Unterstützt { detail: { code, message } }, { code }, und detail == "<code>".
export function extractErrorCode(err) {
  const data = err?.response?.data;
  if (!data) return null;
  if (data.detail && typeof data.detail === "object" && data.detail.code) return data.detail.code;
  if (typeof data.code === "string") return data.code;
  if (typeof data.detail === "string" && Object.values(AGE_ERROR).includes(data.detail)) return data.detail;
  return null;
}

export function extractErrorMessage(err) {
  const data = err?.response?.data;
  if (!data) return null;
  if (data.detail && typeof data.detail === "object") return data.detail.message || null;
  if (typeof data.detail === "string") return data.detail;
  return null;
}
